import { React, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Container, Button, Accordion, Tab, Tabs } from 'react-bootstrap';
import Sections from './Sections';
import { ThreeDots } from 'react-loader-spinner';
import SectionModal from '../Modals/SectionModal';
import { ApiManager } from '../../ApiManager';
import { handleApiError } from '../../utils/apiErrorHandler';
import { RecommendationForm } from '../Forms/RecommendationForm';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { applyReorder } from '../../utils/reorderUtils';

function SortableSectionWrapper({ section, children }) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: section.Id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        visibility: isDragging ? 'hidden' : 'visible',
        display: 'flex',
        alignItems: 'flex-start',
    };
    return (
        <div ref={setNodeRef} style={style}>
            <button
                ref={setActivatorNodeRef}
                {...attributes}
                {...listeners}
                style={{ cursor: 'grab', border: 'none', background: 'none', padding: '10px 6px', color: '#666', fontSize: '16px', flexShrink: 0 }}
                title="Drag to reorder">
                ⠿
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        </div>
    );
}

export const Admin = () => {

    const [sections, setSections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingProjectSections, setIsLoadingProjectSections] = useState(false);
    const [showSectionModal, setShowSectionModal] = useState(false);
    const [activeSectionId, setActiveSectionId] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    useEffect(() => {
        // SectionModal and the delete handler in Sections.jsx call setIsLoading(true) after a
        // successful create/update/delete to trigger a refetch — this effect must re-run
        // whenever isLoading flips back to true, not just once on mount, or the spinner never
        // clears and the new/removed section never appears without a full page reload
        if (isLoading) {
            ApiManager.getInstance().getAllSections().then((result) => {
                setSections(result != null ? [...result].filter(s => s.Enabled).sort((a, b) => a.SortOrder - b.SortOrder) : []);
            }).catch(handleApiError).finally(() => {
                setIsLoading(false);
            });
        }
    }, [isLoading]);

    const handleSectionDragStart = (event) => {
        setActiveSectionId(event.active.id);
    };

    const handleSectionDragEnd = (event) => {
        setActiveSectionId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        // Master catalog: Number is a repositionable label, so it moves with SortOrder
        const result = applyReorder(sections, active.id, over.id, { renumber: true });
        if (!result) return;

        const draggedSection = sections.find(s => s.Id === active.id);
        const previousSections = sections;
        setSections(result.reordered);

        ApiManager.getInstance().updateSection({
            ...draggedSection,
            SortOrder: result.targetSortOrder,
            Number: result.targetNumber
        }).catch((error) => {
            // The write failed, so the optimistic order is now a lie — put it back
            setSections(previousSections);
            handleApiError(error);
        });
    };

    const activeSection = sections.find(s => s.Id === activeSectionId) ?? null;

    return (
        <>
            <AuthenticatedTemplate>
                <Container className="p-5">
                    {
                        isLoading || isLoadingProjectSections ?
                            <div style={{ justifyContent: "center", display: "flex", margin: "0 auto" }}>
                                <ThreeDots color="var(--pc-red)" />
                            </div>
                            :
                            <>
                                <h1>Universal Design Administration</h1>
                                <Tabs className="mt-3" defaultActiveKey="checklist">
                                    <Tab eventKey="checklist" title="Checklist">
                                        <SectionModal editingProject={false} setIsLoading={setIsLoading} showSectionModal={showSectionModal} setShowSectionModal={setShowSectionModal} createOrUpdate={1} />
                                        <div>
                                            <div className={"mt-3 mb-3 text-end"}>
                                                <Button onClick={() => setShowSectionModal(true)} variant="outline-success">Add Section</Button>
                                            </div>
                                            <Accordion defaultActiveKey="0">
                                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleSectionDragStart} onDragEnd={handleSectionDragEnd}>
                                                    <SortableContext items={sections.map(s => s.Id)} strategy={verticalListSortingStrategy}>
                                                        {sections.map((section) =>
                                                            <SortableSectionWrapper key={section.Id} section={section}>
                                                                <Sections setIsLoadingProjectSections={setIsLoadingProjectSections} editingProject={false} setIsLoading={setIsLoading} key={section.Id} section={section} canEditSections={true} />
                                                            </SortableSectionWrapper>
                                                        )}
                                                    </SortableContext>
                                                    <DragOverlay dropAnimation={null}>
                                                        {activeSection && (
                                                            <div style={{ background: 'white', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px 16px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', cursor: 'grabbing' }}>
                                                                {activeSection.Number} | {activeSection.Name}
                                                            </div>
                                                        )}
                                                    </DragOverlay>
                                                </DndContext>
                                            </Accordion>
                                        </div>
                                    </Tab>
                                    <Tab eventKey="recommendations" title="Recommendations">
                                        <RecommendationForm />
                                    </Tab>
                                </Tabs>
                            </>
                    }
                </Container>
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <Container className="p-5">
                    <Navigate to="/" replace />;
                </Container>
            </UnauthenticatedTemplate>
        </>
    );
}
